import { Link } from 'react-router-dom';
import { ChevronRight, Trash2, Shield, Clock, Mail, Phone, MapPin, AlertTriangle, CheckCircle, FileText } from 'lucide-react';

const DeleteAccountEN = () => {
  return (
    <div data-testid="delete-account-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-red-600 to-red-800 text-white pt-32 pb-16">
        <div className="container-custom">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-red-200 text-sm mb-6">
            <Link to="/en" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Delete Account</span>
          </nav>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Trash2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">Delete Account</h1>
              <p className="text-red-200 mt-2">Account Deletion Request</p>
            </div>
          </div>
          
          <p className="text-lg text-red-100 max-w-2xl">
            Complete information about your right to delete your account and personal data on AlbMove.
          </p>
        </div>
      </section>
      
      {/* Content */}
      <section className="py-16">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
              
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-slate-700 leading-relaxed mb-6">
                  In accordance with <strong>Regulation (EU) 2016/679 (GDPR), specifically Article 17 (Right to Erasure)</strong> and <strong>Albanian Law No. 9887/2008 "On the Protection of Personal Data"</strong>, you have the right to request the deletion of your account and associated personal data.
                </p>
                
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Operator Information
                  </h3>
                  <p className="text-slate-700 mb-2">
                    The <strong>AlbMove</strong> application is operated by:
                  </p>
                  <ul className="text-slate-600 space-y-1">
                    <li><strong>Company:</strong> C.S.M. Sh.p.k.</li>
                    <li><strong>Tax ID (NIPT):</strong> M21612019M</li>
                    <li><strong>Registered on:</strong> 12/04/2022</li>
                    <li><strong>Headquarters:</strong> Rruga Muçaj, Building 1, 1032, Vorë, Tirana, Albania</li>
                  </ul>
                </div>
              </div>
              
              {/* 1. How to Delete Your Account */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-lg font-bold">1</span>
                  How to Delete Your Account
                </h2>
                
                <p className="text-slate-700 mb-4">
                  Users can delete their account directly from the AlbMove app by following these steps:
                </p>
                
                <div className="bg-red-50 rounded-xl p-6 border border-red-100 mb-6">
                  <p className="font-semibold text-red-800 text-lg mb-2">
                    Profile → Settings → Delete Account
                  </p>
                  <p className="text-red-700 text-sm">
                    Or you can contact us directly at support@albmove.al
                  </p>
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-3">Deletion Process:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Confirmation of deletion via the app or email</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Account is deactivated immediately after confirmation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Access to the application is automatically blocked</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Complete data deletion is performed within <strong>30 days</strong></span>
                  </li>
                </ul>
              </div>
              
              {/* 2. What Gets Deleted */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-lg font-bold">2</span>
                  What Gets Deleted
                </h2>
                
                <p className="text-slate-700 mb-4">
                  When you delete your account, the following data is permanently deleted:
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'First and last name',
                    'Email address',
                    'Phone number',
                    'Identity documents (if uploaded)',
                    'Profile data',
                    'Device information',
                    'App usage data',
                    'Preferences and settings'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                      <Trash2 className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* 3. What Is Retained for Legal Reasons */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-lg font-bold">3</span>
                  What Is Retained for Legal Reasons
                </h2>
                
                <div className="bg-amber-50 rounded-xl p-6 border border-amber-200 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-800 font-medium mb-2">Legal Obligation</p>
                      <p className="text-amber-700 text-sm">
                        In accordance with <strong>Albanian Law No. 87/2019 on Fiscalization</strong> and Albanian tax legislation, 
                        we are required to retain certain data for legal purposes.
                      </p>
                    </div>
                  </div>
                </div>
                
                <p className="text-slate-700 mb-4">
                  The following data is retained for <strong>10 years</strong> after account deletion:
                </p>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Financial transaction data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Issued fiscal invoices</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Financial records required by law</span>
                  </li>
                </ul>
                
                <p className="text-slate-600 text-sm mt-4 italic">
                  * This data is retained solely for legal compliance and is not used for marketing or other purposes.
                </p>
              </div>
              
              {/* 4. Deletion Limitations */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-lg font-bold">4</span>
                  Deletion Limitations
                </h2>
                
                <p className="text-slate-700 mb-4">
                  Account and data deletion may be limited or delayed in the following cases:
                </p>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Ongoing legal investigations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Official requests from government authorities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Outstanding tax obligations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Incomplete or disputed transactions</span>
                  </li>
                </ul>
              </div>
              
              {/* 5. Deletion Confirmation */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-lg font-bold">5</span>
                  Deletion Confirmation
                </h2>
                
                <p className="text-slate-700 mb-4">
                  Upon completion of the deletion process, you will receive:
                </p>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Confirmation email that the process has started</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Final notification when data has been completely deleted</span>
                  </li>
                </ul>
              </div>
              
              {/* 6. Contact */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-lg font-bold">6</span>
                  Contact for Privacy Issues
                </h2>
                
                <p className="text-slate-700 mb-6">
                  For any questions or requests regarding account deletion or data privacy, please contact us:
                </p>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <a 
                    href="mailto:support@albmove.al" 
                    className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 hover:bg-blue-100 transition-colors"
                  >
                    <Mail className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="text-sm text-blue-600">Email</p>
                      <p className="font-semibold text-slate-900">support@albmove.al</p>
                    </div>
                  </a>
                  
                  <a 
                    href="tel:+355682001818" 
                    className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 hover:bg-blue-100 transition-colors"
                  >
                    <Phone className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="text-sm text-blue-600">Phone</p>
                      <p className="font-semibold text-slate-900">+355 68 200 1818</p>
                    </div>
                  </a>
                  
                  <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                    <MapPin className="w-6 h-6 text-slate-600" />
                    <div>
                      <p className="text-sm text-slate-600">Address</p>
                      <p className="font-semibold text-slate-900 text-sm">Rruga Muçaj, Vorë, Tirana</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Final Statement */}
              <div className="bg-slate-100 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-600 text-sm leading-relaxed">
                  This page has been prepared in accordance with the requirements of <strong>Google Play Developer Policy</strong> and <strong>Apple App Store Guidelines</strong> for user account deletion, as well as Albanian and European legislation for the protection of personal data.
                </p>
              </div>
              
              {/* Last Updated */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <p className="text-slate-500 text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Last updated: {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
              
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DeleteAccountEN;
